import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { Dayjs } from "dayjs";
import MetricTable from "../MetricTable";
import { useMetricsWithCompare } from "../useMetrics";
import { shapeConsultantTable } from "../consultantTable";
import { ConsultantOption, TabProps } from "../types";
import ConsultantDetail from "./ConsultantDetail";

interface ConsultantsTabProps extends TabProps {
  consultants: ConsultantOption[];
  /** Clears / sets the consultant URL param — a row click drills into one. */
  setConsultant: (id: string) => void;
  currentUser: any;
  /** Compare-to-previous-period toggle from the filter bar. */
  compare?: boolean;
  previousRange?: { start: Dayjs | null; end: Dayjs | null } | null;
  previousLabel?: string;
}

/**
 * The consultants x metrics table, replacing Program Statistics' wall of
 * charts. Selecting a consultant (the URL `consultant` param) swaps in their
 * detail view.
 */
const ConsultantsTab: React.FC<ConsultantsTabProps> = ({
  start,
  end,
  consultant,
  onDrill,
  consultants,
  setConsultant,
  currentUser,
  compare = false,
  previousRange = null,
  previousLabel,
}) => {
  const enabled = !consultant;
  const { current, previous } = useMetricsWithCompare(
    { start, end, groupBy: "consultant", enabled },
    compare ? previousRange : null,
  );
  // Ranked columns and the table's own row selection / labels.
  const ranked = useMemo(
    () => (current.data ? shapeConsultantTable(current.data) : current.data),
    [current.data],
  );

  if (consultant) {
    const selected =
      consultants.find((c) => String(c._id) === String(consultant)) ||
      (String(currentUser?._id) === String(consultant)
        ? {
            _id: String(currentUser?._id),
            name: `${currentUser?.firstName || ""} ${
              currentUser?.lastName || ""
            }`.trim(),
            firstName: currentUser?.firstName || "",
            lastName: currentUser?.lastName || "",
          }
        : undefined);
    return (
      <ConsultantDetail
        consultantId={consultant}
        consultantName={selected?.name || "Consultant"}
        consultantUser={selected}
        start={start}
        end={end}
        onDrill={onDrill}
        onBack={() => setConsultant("")}
      />
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#2c2c34", mb: 0.5 }}>
        Consultants
      </Typography>
      <Typography variant="body2" sx={{ color: "#8a8a93", mb: 2 }}>
        Every metric by consultant. Click a name for their detail view, or any
        number to see the records behind it.
      </Typography>
      <MetricTable
        data={ranked}
        loading={current.loading}
        error={current.error}
        previous={compare ? previous.data : null}
        previousLabel={previousLabel}
        exportName="consultant-metrics"
        onGroupClick={(group) => setConsultant(group.key)}
        onCellClick={(row, group) =>
          onDrill({
            id: row.id,
            label: row.label,
            consultant: group ? group.key : undefined,
            consultantLabel: group ? group.label : undefined,
          })
        }
      />
    </Box>
  );
};

export default ConsultantsTab;
